import { useMemo, useState, type ElementType, type ReactNode } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Crown,
  Inbox,
  Info,
  Loader2,
  Medal,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Trophy,
  Zap,
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { SubscriptionTierBadge } from "@/components/teacher/SubscriptionTierBadge";
import { AdBoostPurchaseCard } from "@/components/teacher/AdBoostPurchaseCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useCancelPendingTierChange,
  useCancelSubscription,
  useRenewSubscription,
  useScheduleSubscriptionAtTrialEnd,
  useScheduleTierDowngrade,
  useSubscribeToTier,
  useSubscriptionHistory,
  useSubscriptionPlans,
  useSubscriptionStatus,
  useTurnOffAutoRenew,
  useUpgradeTier,
} from "@/hooks/use-teacher-subscription";
import { isBenefitBearing } from "@/services/teacherSubscriptionService";
import type {
  BillingCycle,
  SubscriptionStatus,
  CurrentSubscriptionResponse,
  PendingChangeResponse,
  PeriodEndedBy,
  RenewReason,
  SubscriptionEventSource,
  ScheduledSubscriptionResponse,
  SubscriptionPlanResponse,
  SubscriptionTier,
  TeacherSubscriptionEventResponse,
  TeacherTrialResponse,
} from "@/services/teacherSubscriptionService";
import { currencySymbol } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { GracePeriodBanner } from "@/components/teacher/GracePeriodBanner";
import { ScheduledPurchaseBanner } from "@/components/teacher/ScheduledPurchaseBanner";
import { formatDaysLeft, formatTrialDate } from "@/lib/trialDate";
import { formatDate, formatDateTime } from "@/lib/subscriptionDate";


function formatMinorPrice(amountMinor: number, sym: string): string {
  return `${sym}${(amountMinor / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

const TIER_ORDER: Record<SubscriptionTier, number> = { SILVER: 1, GOLD: 2, PLATINUM: 3 };

const TIER_ICONS: Record<SubscriptionTier, ElementType> = {
  SILVER: Medal,
  GOLD: Trophy,
  PLATINUM: Crown,
};

const TIER_GRADIENTS: Record<SubscriptionTier, string> = {
  SILVER: "from-slate-600 to-slate-800",
  GOLD: "from-amber-500 to-orange-600",
  PLATINUM: "from-violet-600 to-indigo-700",
};

const TIER_HEADER_COLORS: Record<SubscriptionTier, { icon: string; label: string; badge: string }> = {
  SILVER:   { icon: "text-slate-500",  label: "text-slate-700",  badge: "bg-slate-600"   },
  GOLD:     { icon: "text-amber-500",  label: "text-amber-700",  badge: "bg-amber-500"   },
  PLATINUM: { icon: "text-violet-600", label: "text-violet-700", badge: "bg-violet-600"  },
};

const HISTORY_PAGE_SIZE = 10;

const OFFERING_TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash course",
};

/** Renders a plan's/entitlements' allowed offering types, e.g. ["ONE_TO_ONE"] -> "1-on-1 only". */
function formatOfferingTypes(types: string[] | null | undefined): string {
  if (!types || types.length === 0) return "—";
  const labels = types.map((t) => OFFERING_TYPE_LABELS[t] ?? t.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase()));
  if (labels.length === 1) return `${labels[0]} only`;
  return labels.join(", ");
}

/**
 * Bonus BMC Coins copy (F12). On a yearly billing cycle the coins are credited
 * once, up front, as bonusCoinsPerMonth * 12 -- an explicit product decision,
 * so this must never read as a recurring monthly drip for annual subscribers.
 */
function formatBonusCoins(bonusCoinsPerMonth: number | null | undefined, billingCycle: BillingCycle): string {
  if (!bonusCoinsPerMonth) return "—";
  if (billingCycle === "YEARLY") {
    return `${bonusCoinsPerMonth * 12} coins, credited once at the start of the year`;
  }
  return `+${bonusCoinsPerMonth}/month`;
}

function formatCoinDiscount(coinDiscountPct: number | null | undefined): string {
  if (!coinDiscountPct) return "—";
  return `${coinDiscountPct}% off`;
}

/**
 * Part 1 shipped the two benefit rows below marked "Coming soon", because the backend granted
 * nothing yet. Part 2 removes that badge -- but only once TEACHER_TIER_BENEFITS is actually on.
 * With the flag off the backend still grants nothing (entitlements resolve to the unrestricted
 * baseline), so showing a bare plan value would advertise a benefit the teacher will not receive.
 * The impl-plan states this explicitly: the enforcement and the badge removal ship together, never
 * one without the other.
 */
function PendingBenefit({ value, live }: { value: string; live: boolean }) {
  if (live) return <>{value}</>;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-slate-600">{value}</span>
      <Badge variant="outline" className="text-xs leading-none">
        Coming soon
      </Badge>
    </div>
  );
}

/**
 * The tier the platform steers teachers toward. Gold is the first tier that unlocks the full
 * Insights/benchmarks surface, which is the step most teachers are actually buying.
 */
const RECOMMENDED_TIER = "GOLD";

/** The tier the free trial grants. Only this column gets the trial treatment. */
const TRIAL_TIER = "PLATINUM";

/**
 * Outlines the recommended tier's column. A table cannot be outlined as a unit, so every cell in
 * the column carries the side borders and the first/last close the top and bottom -- under
 * border-collapse the adjacent cell borders merge into one continuous outline rather than a stack
 * of boxes.
 *
 * No rounded corners: the table sets border-collapse, under which border-radius on a cell is
 * ignored outright, so a rounded-* class here would be dead styling that reads as intent.
 *
 * Violet is the platform primary (#7C3AED / violet-600 in MASTER.md). The slate neutrals and the
 * amber grace/warning states elsewhere on this page are deliberately untouched: per ADR 133 this
 * is a logged-in dashboard tab, not a conversion-oriented pricing landing page.
 */
type ColumnHighlight = "current" | "trial" | "recommended" | "none";

/**
 * What a tier's column is marked as. The teacher's own tier wins over the recommendation: once
 * they hold Gold, "you are here" is the more useful thing to say than "we suggest this".
 *
 * `currentTier` must already be null for a subscription that is no longer benefit-bearing -- see
 * `currentTier` in the page body.
 */
function columnHighlight(tier: string, currentTier: string | null, trialActive: boolean): ColumnHighlight {
  // The trial is a third state, not a cheaper "current": the teacher holds Platinum today but has
  // bought nothing, so neither "Your plan" nor a plain "Recommended" would be true.
  if (trialActive && tier === TRIAL_TIER) return "trial";
  if (currentTier && tier === currentTier) return "current";
  if (tier === RECOMMENDED_TIER) return "recommended";
  return "none";
}

/**
 * Outlines a marked tier's column. A table cannot be outlined as a unit, so every cell carries the
 * side borders and the first/last close the top and bottom; under border-collapse those merge into
 * one continuous outline. No rounded corners -- border-collapse makes border-radius on a cell
 * inert, so a rounded-* class would be dead styling that reads as intent.
 *
 * Current-plan columns use the tier's own color palette so the highlight feels like an extension
 * of the tier identity rather than an unrelated green. Recommended (non-current) uses violet
 * (the platform primary, #7C3AED) -- the two marks can be on screen at once so they must differ.
 *
 * Every class is written out in full. Tailwind scans source for literal class names, so a
 * constructed one like `border-t-${tone}` is never generated and the colour silently vanishes.
 */
const TIER_COLUMN_CURRENT: Record<SubscriptionTier, { base: string; top: string; bottom: string }> = {
  SILVER:   { base: "bg-slate-50/80 border-x border-slate-300",   top: "border-t-2 border-t-slate-500",   bottom: "border-b-2 border-b-slate-500"   },
  GOLD:     { base: "bg-amber-50/70 border-x border-amber-300",   top: "border-t-2 border-t-amber-500",   bottom: "border-b-2 border-b-amber-500"   },
  PLATINUM: { base: "bg-violet-50/70 border-x border-violet-300", top: "border-t-2 border-t-violet-600", bottom: "border-b-2 border-b-violet-600" },
};

function tierColumn(
  tier: string,
  currentTier: string | null,
  trialActive: boolean,
  edge: "top" | "middle" | "bottom"
): string {
  const highlight = columnHighlight(tier, currentTier, trialActive);
  if (highlight === "none") return "";
  if (highlight === "trial") {
    const base = "bg-violet-50/70 border-x border-dashed border-violet-400";
    if (edge === "top")    return `${base} border-t-2 border-dashed border-t-violet-600`;
    if (edge === "bottom") return `${base} border-b-2 border-dashed border-b-violet-600`;
    return base;
  }
  if (highlight === "current") {
    const c = TIER_COLUMN_CURRENT[tier as SubscriptionTier] ?? TIER_COLUMN_CURRENT.SILVER;
    if (edge === "top")    return `${c.base} ${c.top}`;
    if (edge === "bottom") return `${c.base} ${c.bottom}`;
    return c.base;
  }
  // recommended
  const base = "bg-violet-50/70 border-x border-violet-200";
  if (edge === "top")    return `${base} border-t-2 border-t-violet-600`;
  if (edge === "bottom") return `${base} border-b-2 border-b-violet-600`;
  return base;
}

/**
 * Analytics access (AC#28). Unlike the other benefits in this table, this is NOT an
 * admin-configurable plan column -- it is policy in TeacherAnalyticsService
 * (`benchmarksVisible = GOLD || PLATINUM`), so it is derived from the tier name here rather than
 * read from plan data.
 *
 * Deliberately describes only what is actually built. The requirement also gives Platinum
 * "prior-period trend indicators, exportable data" over Gold, but no such gating or export exists
 * in the backend today -- Gold and Platinum resolve identically -- so this row does not claim a
 * difference the product would not deliver.
 */
function formatAnalyticsAccess(tier: string | null | undefined): string {
  return tier === "GOLD" || tier === "PLATINUM" ? "Insights & benchmarks" : "Basic summary";
}

/**
 * The backend signals "no cap" with Java's Integer.MAX_VALUE and passes it through unremapped, so
 * rendering it raw prints 2147483647 at the teacher. That value reaches the UI whenever entitlements
 * resolve to the unrestricted shape -- most visibly while TEACHER_TIER_BENEFITS is on in the
 * frontend but not yet in the backend, which is precisely the window a staged rollout passes
 * through.
 */
const UNCAPPED_OFFERINGS = 2147483647; // Integer.MAX_VALUE

function formatMaxOfferings(maxPublishedOfferings: number | null | undefined): string {
  if (maxPublishedOfferings === null || maxPublishedOfferings === undefined) return "—";
  if (maxPublishedOfferings >= UNCAPPED_OFFERINGS) return "Unlimited";
  return String(maxPublishedOfferings);
}

/** Title-cases a SCREAMING_SNAKE_CASE event/eventType code for display, e.g. "AD_BOOST_PURCHASED" -> "Ad boost purchased". */
function formatCode(code: string): string {
  const words = code.toLowerCase().split("_");
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

const EVENT_SOURCE_LABELS: Record<SubscriptionEventSource, string> = {
  MANUAL: "Manual",
  AUTO: "Automatic",
  SCHEDULED: "Scheduled",
  ADMIN: "Admin",
  SYSTEM: "System",
};

/**
 * The current subscription's status, in words rather than the raw enum.
 *
 * CANCELLED is the one that matters: it does NOT mean "over". Benefits run until endDate (AC#11),
 * so printing "CANCELLED" beside a live tier tells a teacher their subscription has ended when it
 * has not. The History tab already draws this distinction via CANCEL_PENDING -> "Cancelling"; this
 * brings the top card into the same vocabulary.
 */
function subscriptionStatusLabel(status: SubscriptionStatus): string {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "CANCELLED":
      return "Cancelling";
    case "EXPIRED":
      return "Expired";
    default:
      return status;
  }
}

const PERIOD_ENDED_LABELS: Record<PeriodEndedBy, string> = {
  ACTIVE: "Active",
  CANCEL_PENDING: "Cancelling",
  UPGRADED: "Upgraded",
  DOWNGRADED: "Downgraded",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
  DISPUTE_LOST: "Payment disputed",
};

/**
 * F13 (AC#75): CANCEL_PENDING must never read as plain "Active" -- it means the
 * teacher already cancelled but the period is still running out, and a
 * departing teacher seeing bare "Active" would be told something false.
 */
function PeriodStatusBadge({ endedBy }: { endedBy: PeriodEndedBy }) {
  if (endedBy === "ACTIVE") {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
        Active
      </Badge>
    );
  }
  if (endedBy === "CANCEL_PENDING") {
    return (
      <Badge variant="warning" className="gap-1">
        <Clock className="w-3 h-3" aria-hidden="true" />
        Cancelling
      </Badge>
    );
  }
  if (endedBy === "DISPUTE_LOST") {
    return <Badge variant="destructive">{PERIOD_ENDED_LABELS[endedBy]}</Badge>;
  }
  return <Badge variant="secondary">{PERIOD_ENDED_LABELS[endedBy]}</Badge>;
}

interface RenewCopy {
  title: string;
  body: string;
  cta: string;
}

/** F14: the server decides renewability (`renewable`); this only picks the copy variant from `renewReason`. */
function renewCopyFor(reason: RenewReason, tier: SubscriptionTier, endDate: string | null): RenewCopy {
  switch (reason) {
    case "EXPIRED":
      return {
        title: "Your subscription has expired",
        body: `Renew now to restore your ${tier} benefits and ranking boost.`,
        cta: "Renew subscription",
      };
    case "PAYMENT_HALTED":
      return {
        title: "We couldn't process your last payment",
        body: `Renew now to avoid losing your ${tier} benefits.`,
        cta: "Retry payment",
      };
    case "CANCELLED_ENDING":
    default:
      return {
        title: "Your subscription is ending",
        body: `Renew now to keep your ${tier} benefits going past ${formatDate(endDate)}.`,
        cta: "Renew now",
      };
  }
}


/** F13: labels an ad-boost lifecycle event as Manual/Auto per its `source` -- every other event just shows its source. */
function eventSourceBadgeLabel(event: TeacherSubscriptionEventResponse): string {
  const isAdBoost = event.eventType.includes("AD_BOOST") || event.relatedAdBoostPurchaseId !== null;
  if (isAdBoost && (event.source === "MANUAL" || event.source === "AUTO")) {
    return event.source === "MANUAL" ? "Manual" : "Auto";
  }
  return EVENT_SOURCE_LABELS[event.source];
}

interface EventRowProps {
  event: TeacherSubscriptionEventResponse;
}

function eventDotColor(eventType: string): string {
  if (eventType.includes("SUBSCRIBED") || eventType.includes("RENEWED") || eventType.includes("UPGRADED")) return "bg-emerald-500";
  // F7: AUTO_RENEW_DISABLED matches none of the rules below and would fall through to the grey
  // fallback. Amber, like a scheduled downgrade: the teacher keeps everything they paid for until
  // the end date -- it is a future change of state, not a loss of benefits today.
  if (eventType.includes("AUTO_RENEW_DISABLED")) return "bg-amber-400";
  if (eventType.includes("CANCELLED") || eventType.includes("EXPIRED") || eventType.includes("DISPUTE")) return "bg-rose-400";
  if (eventType.includes("DOWNGRADE")) return "bg-amber-400";
  if (eventType.includes("AD_BOOST")) return "bg-violet-400";
  return "bg-slate-300";
}

function EventRow({ event }: EventRowProps) {
  const dotColor = eventDotColor(event.eventType);
  return (
    <li className="flex gap-4 px-5 py-4">
      {/* Timeline spine */}
      <div className="flex flex-col items-center shrink-0 pt-1">
        <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", dotColor)} aria-hidden="true" />
        <div className="w-px flex-1 bg-slate-100 mt-1.5" aria-hidden="true" />
      </div>
      {/* Content */}
      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-slate-800 leading-snug">{formatCode(event.eventType)}</p>
          <Badge variant="outline" className="shrink-0 text-xs leading-none font-medium">
            {eventSourceBadgeLabel(event)}
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(event.occurredAt)}</p>
        {event.detail && (
          <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg px-3 py-2 leading-relaxed">{event.detail}</p>
        )}
      </div>
    </li>
  );
}

function Feature({ text, label, value, icon, muted }: {
  text?: string;
  label?: string;
  value?: string;
  icon?: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      {icon ?? <CheckCircle2 className={cn("w-4 h-4 shrink-0", muted ? "text-slate-300" : "text-emerald-500")} aria-hidden="true" />}
      {label !== undefined && value !== undefined ? (
        <div className="flex items-center justify-between w-full gap-2">
          <span className="text-xs text-slate-400">{label}</span>
          <span className={cn("text-sm font-semibold", muted ? "text-slate-400" : "text-slate-800")}>{value}</span>
        </div>
      ) : (
        <span className={cn("text-sm leading-snug", muted ? "text-slate-400" : "text-slate-700")}>{text}</span>
      )}
    </div>
  );
}

interface PlanPriceCellProps {
  plan: SubscriptionPlanResponse;
  billingCycle: BillingCycle;
  sym: string;
}

function PlanPriceCell({ plan, billingCycle, sym }: PlanPriceCellProps) {
  const isYearly = billingCycle === "YEARLY";
  const priceMinor = isYearly ? plan.yearlyPriceMinor : plan.monthlyPriceMinor;
  const discountPct = isYearly ? plan.yearlyDiscountPct : plan.monthlyDiscountPct;
  const hasDiscount = discountPct > 0;
  const faceMinor = hasDiscount ? Math.round(priceMinor / (1 - discountPct / 100)) : priceMinor;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-black text-slate-900">{formatMinorPrice(priceMinor, sym)}</span>
        <span className="text-sm text-slate-400">/{isYearly ? "yr" : "mo"}</span>
      </div>
      {hasDiscount && (
        <div className="flex items-center gap-1.5">
          <span className="text-sm line-through text-slate-300">{formatMinorPrice(faceMinor, sym)}</span>
          <span className="text-sm font-bold text-emerald-600">-{discountPct}%</span>
        </div>
      )}
    </div>
  );
}

interface PlanActionCellProps {
  plan: SubscriptionPlanResponse;
  subscription: CurrentSubscriptionResponse | null;
  /** The active Platinum trial, or null. Changes what EVERY column offers -- see below. */
  trial: TeacherTrialResponse | null;
  pendingChange: PendingChangeResponse | null;
  boostOverridden: boolean;
  billingCycle: BillingCycle;
  /** The currency symbol the plan grid already renders prices with -- AC#4 uses the same amount. */
  sym: string;
  /** TEACHER_SUBSCRIPTION_AUTO_RENEW_CHOICE. Off => today's bare Subscribe button, no dialog. */
  autoRenewChoiceOn: boolean;
  subscribeToTier: ReturnType<typeof useSubscribeToTier>;
  scheduleAtTrialEnd: ReturnType<typeof useScheduleSubscriptionAtTrialEnd>;
  upgradeTier: ReturnType<typeof useUpgradeTier>;
  renewSubscription: ReturnType<typeof useRenewSubscription>;
  scheduleDowngrade: ReturnType<typeof useScheduleTierDowngrade>;
  cancelSubscription: ReturnType<typeof useCancelSubscription>;
  className?: string;
}

function PlanActionCell({
  plan,
  subscription,
  trial,
  pendingChange,
  boostOverridden,
  billingCycle,
  sym,
  autoRenewChoiceOn,
  subscribeToTier,
  scheduleAtTrialEnd,
  upgradeTier,
  renewSubscription,
  scheduleDowngrade,
  cancelSubscription,
  className = "",
}: PlanActionCellProps) {
  /**
   * AC#2 / ADR-151: the consent starts FALSE on every open of the dialog and is never derived from
   * a previous purchase or remembered across one -- see the `onOpenChange` reset below.
   */
  const [autoRenewConsent, setAutoRenewConsent] = useState(false);

  // On a trial: every tier is bought for later, never now.
  //
  // "Subscribe" is not an option during a trial -- POST /subscription/order 409s with
  // SUBSCRIPTION_SCHEDULING_REQUIRED -- so offering it would be a button that can only fail.
  // PLATINUM is included deliberately (AC#18): a teacher on the Platinum trial is exactly the
  // teacher most likely to want to keep Platinum, and nothing else on this page lets them.
  if (trial?.active) {
    const isPending = scheduleAtTrialEnd.isPending && scheduleAtTrialEnd.variables?.planId === plan.planId;
    return (
      <Button
        size="sm"
        data-testid={`plan-action-${plan.tier.toLowerCase()}`}
        className={cn("w-full cursor-pointer transition-colors duration-200", className)}
        disabled={boostOverridden || scheduleAtTrialEnd.isPending}
        onClick={() => scheduleAtTrialEnd.mutate({ planId: plan.planId, billingCycle })}
      >
        {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
        {/* No date on this CTA. It used to read "Starts <trial end date>" -- the date the
            PURCHASED tier would begin -- which was repeatedly misread as the trial's own start
            date, making a trial granted today look like it began three months out. The timing is
            still stated where it cannot be mistaken for the trial's: the confirm dialog, and the
            scheduled-purchase banner ("Gold starts 15 Jan 2027, when your trial ends"). */}
        Choose plan
      </Button>
    );
  }

  // Not subscribed at all yet -- or holding a subscription that no longer bears benefits.
  //
  // The status guard matters: GET /subscription falls back to the teacher's most recent
  // subscription when none is benefit-bearing, so `subscription` is non-null after one EXPIRES.
  // Without this, that teacher's old tier still rendered a "Current Plan" badge and a cancel
  // dialog for a subscription they no longer hold, instead of the Subscribe button that would let
  // them buy it again.
  if (!subscription || !isBenefitBearing(subscription.status)) {
    const isPending = subscribeToTier.isPending && subscribeToTier.variables?.planId === plan.planId;
    const isYearly = billingCycle === "YEARLY";
    // AC#4: the amount stated in the dialog is the plan card's own amount for the selected cycle --
    // the same derivation PlanPriceCell renders, so the two can never disagree.
    const price = formatMinorPrice(isYearly ? plan.yearlyPriceMinor : plan.monthlyPriceMinor, sym);
    const period = isYearly ? "year" : "month";
    const consentId = `auto-renew-consent-${plan.tier.toLowerCase()}`;

    const subscribeButton = (
      <Button
        size="sm"
        data-testid={`plan-action-${plan.tier.toLowerCase()}`}
        className={cn("w-full cursor-pointer transition-colors duration-200", className)}
        disabled={boostOverridden || subscribeToTier.isPending}
        onClick={
          autoRenewChoiceOn
            ? undefined // the AlertDialogTrigger owns the click; the purchase happens on confirm
            : () => subscribeToTier.mutate({ planId: plan.planId, billingCycle })
        }
      >
        {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Subscribe
      </Button>
    );

    // Flag off: today's behaviour byte-for-byte -- the bare button fires the mutation directly and
    // `autoRenew` is omitted from the request, so the backend creates a mandate exactly as it does
    // today. (The data-testid is added unconditionally; it is not part of the behaviour.)
    if (!autoRenewChoiceOn) {
      return subscribeButton;
    }

    // A dialog rather than an inline checkbox: this cell renders once per column, so an inline
    // control would put three independent consent states on screen at once.
    return (
      <AlertDialog
        onOpenChange={(open) => {
          // AC#2 / U10: unchecked on EVERY open, including a reopen after a dismissed Razorpay
          // modal. The consent is never remembered.
          if (open) setAutoRenewConsent(false);
        }}
      >
        <AlertDialogTrigger asChild>{subscribeButton}</AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm your {plan.tier} subscription</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll be charged {price} once for 1 {period} of {plan.tier}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id={consentId}
                className="mt-0.5 cursor-pointer"
                checked={autoRenewConsent}
                onCheckedChange={(checked) => setAutoRenewConsent(checked === true)}
              />
              <div>
                <Label htmlFor={consentId} className="text-sm font-semibold text-slate-800 cursor-pointer">
                  Renew automatically every {period}
                </Label>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
                  Authorise BookMyCoaching to charge {price} to this payment method every {period} until you
                  turn this off. You can turn auto-renew off at any time; it cannot be turned back on.
                </p>
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={() =>
                subscribeToTier.mutate({ planId: plan.planId, billingCycle, autoRenew: autoRenewConsent })
              }
            >
              Continue to payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Currently on this exact tier.
  if (subscription.tier === plan.tier) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className={cn("w-full cursor-pointer text-destructive border-destructive/30 hover:bg-destructive/10 transition-colors duration-200", className)}
            disabled={boostOverridden || cancelSubscription.isPending}
          >
            {cancelSubscription.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Cancel Subscription
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll keep your {plan.tier} benefits until {formatDate(subscription.endDate)}. Your
              subscription won&apos;t renew after that, and no refund is issued for the current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => cancelSubscription.mutate()}
            >
              Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  const targetOrder = TIER_ORDER[plan.tier];
  const currentOrder = TIER_ORDER[subscription.tier];

  // Currently subscribed to a lower tier — this column is an upgrade.
  if (targetOrder > currentOrder) {
    // A CANCELLED subscription still bears benefits until endDate, so it reaches this branch --
    // but createTierChangeOrder refuses it with 409 SUBSCRIPTION_CANCELLED, because charging a
    // prorated difference for a tier the teacher loses at endDate takes money for something about
    // to disappear. Offering "Upgrade" here was a button that could only ever fail.
    //
    // Renewing is the supported route back to ACTIVE, and upgrading works normally afterwards, so
    // this offers that instead of a dead end. The server keeps the refusal; this just stops the UI
    // contradicting it.
    if (subscription.status === "CANCELLED") {
      return (
        <Button
          size="sm"
          variant="outline"
          className={cn("w-full cursor-pointer transition-colors duration-200", className)}
          disabled={boostOverridden || renewSubscription.isPending}
          title={`Your subscription is cancelled, so it can't be upgraded. Renew it first, then upgrade to ${plan.tier}.`}
          onClick={() => renewSubscription.mutate()}
        >
          {renewSubscription.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Renew to upgrade
        </Button>
      );
    }

    const isPending = upgradeTier.isPending && upgradeTier.variables?.targetPlanId === plan.planId;
    return (
      <Button
        size="sm"
        className={cn("w-full cursor-pointer transition-colors duration-200", className)}
        disabled={boostOverridden || upgradeTier.isPending}
        onClick={() => upgradeTier.mutate({ targetPlanId: plan.planId })}
      >
        {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Upgrade
      </Button>
    );
  }

  // Currently subscribed to a higher tier — this column is a downgrade.
  const isPending = scheduleDowngrade.isPending && scheduleDowngrade.variables?.targetPlanId === plan.planId;
  return (
    <Button
      size="sm"
      variant="outline"
      className={cn("w-full cursor-pointer transition-colors duration-200", className)}
      disabled={boostOverridden || scheduleDowngrade.isPending || Boolean(pendingChange)}
      title={pendingChange ? "A downgrade is already scheduled — cancel it first to change your selection" : undefined}
      onClick={() => scheduleDowngrade.mutate({ targetPlanId: plan.planId })}
    >
      {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {pendingChange ? "Downgrade scheduled" : "Downgrade"}
    </Button>
  );
}

export default function TeacherSubscriptionPage() {
  usePageTitle('Subscription | BookMyCoaching');
  const { teacherProfile } = useTeacherProfile();
  const teacherName = teacherProfile?.fullName ?? "";
  const teacherAvatar = teacherProfile?.profileImage ?? "";

  const { data: plansData, isLoading: plansLoading } = useSubscriptionPlans();
  const { data: status, isLoading: statusLoading, isError: statusFailed } = useSubscriptionStatus();

  const [billingCycle, setBillingCycle] = useState<BillingCycle>("MONTHLY");
  const [historyPage, setHistoryPage] = useState(0);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("ALL");

  const subscribeToTier = useSubscribeToTier();
  const upgradeTier = useUpgradeTier();
  const scheduleDowngrade = useScheduleTierDowngrade();
  const cancelPending = useCancelPendingTierChange();
  const cancelSubscription = useCancelSubscription();
  const turnOffAutoRenew = useTurnOffAutoRenew();
  const renewSubscription = useRenewSubscription();
  const scheduleAtTrialEnd = useScheduleSubscriptionAtTrialEnd();

  const tierBenefitsOn = isReleaseFlagOn("TEACHER_TIER_BENEFITS");
  /**
   * ADR-151/152. Off => this page behaves exactly as it does today: a bare Subscribe button that
   * always buys a mandate, and an "Auto-renew: On/Off" cell. On => explicit consent at checkout,
   * the "Renews {date}" / "Ends {date}" state, and the one-way turn-off action.
   */
  const autoRenewChoiceOn = isReleaseFlagOn("TEACHER_SUBSCRIPTION_AUTO_RENEW_CHOICE");

  const { data: historyData, isLoading: historyLoading } = useSubscriptionHistory(historyPage, HISTORY_PAGE_SIZE);

  /**
   * The trial, and the purchase that may be waiting behind it, both from server truth. No release
   * flag is consulted: the backend does not read one when resolving a trial, so gating this on a
   * flag would show a teacher with a live trial a "Subscribe" button that 409s while hiding the
   * /scheduled endpoints that do work. Before launch no teacher has a trial, so it stays inert.
   */
  const trial = status?.trial ?? null;
  const trialActive = Boolean(trial?.active);
  const scheduledSubscription = status?.scheduledSubscription ?? null;

  /**
   * CAREFUL: once a trialling teacher schedules a purchase, GET /subscription returns that
   * future-dated row in `subscription` as well as in `scheduledSubscription` -- ACTIVE, but not
   * started. Reading it as the current plan marks a tier the teacher is not yet on as "Your plan"
   * and offers "Cancel Subscription" for a subscription that has not begun. Everything below
   * therefore reads `subscription` as "the plan in force today", which during a trial is none.
   */
  const rawSubscription = status?.subscription ?? null;
  const subscription = trialActive ? null : rawSubscription;
  /**
   * The tier the teacher is actually on, or null. Guarded on status, not just presence:
   * GET /subscription falls back to the most recent subscription when none is benefit-bearing, so
   * `subscription` is non-null for a teacher whose plan has EXPIRED. Marking that column "Current
   * plan" would tell someone whose subscription lapsed that they still hold it.
   */
  const currentTier = subscription && isBenefitBearing(subscription.status) ? subscription.tier : null;
  const pendingChange = status?.pendingChange ?? null;
  const boostOverridden = status?.boostOverridden ?? false;
  const showRenew = tierBenefitsOn && Boolean(subscription) && status?.renewable === true;

  const sortedPlans = useMemo(
    () => [...(plansData?.plans ?? [])].sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier]),
    [plansData]
  );
  const sym = currencySymbol(plansData?.currency);
  const trialPlan = useMemo(
    () => sortedPlans.find((plan) => plan.tier === TRIAL_TIER) ?? null,
    [sortedPlans]
  );

  const renewCopy = useMemo(
    () => (subscription ? renewCopyFor(status?.renewReason ?? null, subscription.tier, subscription.endDate) : null),
    [subscription, status?.renewReason]
  );

  /**
   * The turn-off row's sentence. The amount comes from the plan grid's own numbers for the cycle
   * the teacher is actually billed on -- no new data source and no extra request. Falls back to a
   * date-only sentence while the plans query is still in flight.
   */
  const nextChargeCopy = useMemo(() => {
    if (!subscription) return null;
    const when = formatTrialDate(subscription.endDate);
    const plan = sortedPlans.find((p) => p.tier === subscription.tier);
    if (!plan) return `Your subscription renews on ${when}.`;
    const priceMinor = subscription.billingCycle === "YEARLY" ? plan.yearlyPriceMinor : plan.monthlyPriceMinor;
    return `We'll charge ${formatMinorPrice(priceMinor, sym)} on ${when}.`;
  }, [subscription, sortedPlans, sym]);

  const eventTypeOptions = useMemo(
    () => Array.from(new Set((historyData?.events.content ?? []).map((e) => e.eventType))).sort(),
    [historyData]
  );
  const filteredEvents = useMemo(() => {
    const content = historyData?.events.content ?? [];
    return eventTypeFilter === "ALL" ? content : content.filter((e) => e.eventType === eventTypeFilter);
  }, [historyData, eventTypeFilter]);

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
      <DashboardTopNav role="teacher" />
      <div className="min-h-screen flex w-full pt-14">
        <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} />
        <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto bg-slate-50">
          {/* ── Header ── */}
          <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100">
            <h1 className="text-2xl font-bold text-slate-900">Subscription</h1>
            <p className="text-sm text-slate-500">
              Boost your ranking in student search results with a paid subscription tier.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-5">
            <GracePeriodBanner />

            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview" className="cursor-pointer">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="history" className="cursor-pointer">
                  History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-5 mt-4">
                {/* ── Current status ── */}
                {statusLoading ? (
                  /* A skeleton has no accessible name, so the selector contract gives it a testid. */
                  <Skeleton data-testid="subscription-status-skeleton" className="h-28 w-full rounded-2xl" />
                // Flag-gated like every other branch of this feature: with the flag off this page
                // must behave byte-for-byte as it does today, where a failed load falls through
                // to the "No active subscription" card. That fallback is genuinely wrong -- a
                // teacher could re-subscribe while already subscribed -- but it is pre-existing,
                // and fixing it ungated would change prod behaviour while the flag is off.
                ) : autoRenewChoiceOn && statusFailed ? (
                  /* A failed load is NOT the empty state: telling a paying teacher "No active
                     subscription" because their request failed is exactly the wrong answer. */
                  <div
                    role="alert"
                    className="rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle className="w-6 h-6 text-rose-500" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-base text-slate-900">We couldn&rsquo;t load your subscription</h3>
                    <p className="text-sm text-slate-500 max-w-sm mx-auto mt-1 leading-relaxed">
                      Please refresh the page. If this keeps happening, contact support.
                    </p>
                  </div>
                ) : trialActive && trial ? (
                  /* AC#8 — the trial summary stands in for the subscription card: there is no
                     subscription to describe, and "No active subscription" would be flatly wrong
                     for a teacher currently holding full Platinum limits. */
                  <section className="rounded-2xl border border-violet-200 bg-violet-50/70 p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <SubscriptionTierBadge tier="PLATINUM" trial />
                      <span className="text-sm font-semibold text-violet-900">
                        Started {formatTrialDate(trial.startDate)} · ends {formatTrialDate(trial.endsAt)}
                        {" · "}{formatDaysLeft(trial.daysRemaining)}
                      </span>
                    </div>
                    {trialPlan && (
                      <p className="text-sm text-violet-800 mt-2">
                        Trial includes {formatMaxOfferings(trialPlan.maxPublishedOfferings)} offerings,{" "}
                        {formatOfferingTypes(trialPlan.allowedOfferingTypes)}, and full analytics.
                      </p>
                    )}
                    <p className="text-sm text-violet-800">Search boost is not included during the trial.</p>
                  </section>
                ) : subscription ? (
                  <div className={cn("rounded-2xl p-5 bg-gradient-to-br", TIER_GRADIENTS[subscription.tier])}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <SubscriptionTierBadge tier={subscription.tier} className="bg-white/20 border-white/30 text-white" />
                      <span
                        className={cn(
                          "text-xs font-semibold px-2.5 py-0.5 rounded-full",
                          subscription.status === "ACTIVE"
                            ? "bg-white/20 text-white"
                            : "bg-white/10 text-white/70"
                        )}
                      >
                        {subscriptionStatusLabel(subscription.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2 pt-4 border-t border-white/20">
                      <div>
                        <p className="text-xs text-white/60 font-medium mb-1">Start date</p>
                        <p className="text-sm font-bold text-white">{formatDate(subscription.startDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 font-medium mb-1">End date</p>
                        <p className="text-sm font-bold text-white">{formatDate(subscription.endDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 font-medium mb-1">Billing cycle</p>
                        <p className="text-sm font-bold text-white">
                          {subscription.billingCycle === "MONTHLY" ? "Monthly" : "Yearly"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 font-medium mb-1">Auto-renew</p>
                        {autoRenewChoiceOn ? (
                          /* AC#5: the state a teacher actually needs -- what happens on the end
                             date -- rather than the value of a boolean. Contract: a plain <p>
                             with no role and no accessible name, queried by test id. */
                          <p data-testid="subscription-renewal-state" className="text-sm font-bold text-white">
                            {subscription.autoRenew ? "Renews" : "Ends"} {formatTrialDate(subscription.endDate)}
                          </p>
                        ) : (
                          <p className="text-sm font-bold text-white">
                            {subscription.autoRenew ? "On" : "Off"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* AC#9-13: the one-way turn-off. INSIDE the card, below the grid -- the renew
                        and pending-change banners already occupy the slot beneath it. Never a
                        Switch (AC#10): the action cannot be undone, so it must not be presented
                        as a settable boolean, and nothing anywhere turns it back on. */}
                    {autoRenewChoiceOn && subscription.status === "ACTIVE" && subscription.autoRenew && (
                      <div className="mt-4 pt-4 border-t border-white/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-sm text-white/90 leading-relaxed">{nextChargeCopy}</p>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="shrink-0 w-full sm:w-auto whitespace-nowrap cursor-pointer bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white transition-colors duration-200"
                              disabled={boostOverridden || turnOffAutoRenew.isPending}
                            >
                              {turnOffAutoRenew.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                              Turn off auto-renew
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Turn off auto-renew?</AlertDialogTitle>
                              <AlertDialogDescription>
                                You&apos;ll keep {subscription.tier}, your search boost and your badge until{" "}
                                {formatTrialDate(subscription.endDate)}. We won&apos;t charge you again. This
                                can&apos;t be undone — to keep {subscription.tier} after that date you&apos;d need
                                to subscribe again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              {/* The safe option sits in the Cancel slot, so Escape keeps the mandate. */}
                              <AlertDialogCancel className="cursor-pointer">Keep auto-renew</AlertDialogCancel>
                              <AlertDialogAction
                                className="cursor-pointer"
                                onClick={() => turnOffAutoRenew.mutate()}
                              >
                                Turn it off
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 p-8 text-center shadow-md">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                      <Crown className="w-7 h-7 text-white" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-white text-base mb-1">No active subscription</h3>
                    <p className="text-sm text-white/80 max-w-sm mx-auto leading-relaxed">
                      Upgrade to a paid tier to boost your ranking in student search results and unlock more features.
                    </p>
                  </div>
                )}

                {/* ── AC#19: a purchase waiting for the trial to end ── */}
                {trialActive && scheduledSubscription && (
                  <ScheduledPurchaseBanner scheduled={scheduledSubscription} trialEndsAt={trial?.endsAt ?? null} />
                )}

                {/* ── F14: renew banner -- server decides renewability via `renewable`, never `autoRenew` ── */}
                {showRenew && subscription && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between overflow-hidden relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-l-2xl" aria-hidden="true" />
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-amber-900">{renewCopy?.title}</p>
                        <p className="text-sm text-amber-800 mt-0.5">{renewCopy?.body}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => renewSubscription.mutate()}
                      disabled={renewSubscription.isPending}
                      className="shrink-0 cursor-pointer bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-200"
                    >
                      {renewSubscription.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" aria-hidden="true" />
                      )}
                      {renewCopy?.cta}
                    </Button>
                  </div>
                )}

                {/* ── F4: pending-change banner (AC#51) ── */}
                {pendingChange && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between overflow-hidden relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-l-2xl" aria-hidden="true" />
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-amber-900">
                          Downgrade to {pendingChange.targetTier} scheduled
                        </p>
                        <p className="text-sm text-amber-800 mt-0.5">
                          Your plan changes to {pendingChange.targetTier} on{" "}
                          {formatDate(pendingChange.effectiveDate)}. You&apos;ll keep your current benefits until
                          then.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => cancelPending.mutate()}
                      disabled={cancelPending.isPending}
                      className="shrink-0 cursor-pointer bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-200"
                    >
                      {cancelPending.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                      Keep my plan
                    </Button>
                  </div>
                )}

                {/* ── F4b: admin-override disclosure ── */}
                {boostOverridden && (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5 flex items-start gap-3 overflow-hidden relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500 rounded-l-2xl" aria-hidden="true" />
                    <ShieldAlert className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-indigo-900">
                        Your ranking boost is set by an administrator
                      </p>
                      <p className="text-sm text-indigo-800 mt-0.5">
                        An admin has manually overridden your search ranking boost score. Tier changes below won&apos;t
                        change your ranking while this override is active — contact support if you have questions.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Billing cycle toggle ── */}
                <div className="inline-flex items-center rounded-xl bg-slate-100 p-1 gap-1">
                  <button type="button" onClick={() => setBillingCycle("MONTHLY")} className={cn("px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer", billingCycle === "MONTHLY" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>Monthly</button>
                  <button type="button" onClick={() => setBillingCycle("YEARLY")} className={cn("px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer flex items-center gap-2", billingCycle === "YEARLY" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
                    Yearly
                    <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Save up to 20%</span>
                  </button>
                </div>

                {/* ── Plan cards ── */}
                {plansLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">

                    {/* FREE card */}
                    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden">
                      <div className="h-1.5 bg-slate-400" />
                      <div className="px-5 pt-5 pb-4">
                        <div className="flex items-center gap-2.5 mb-4">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                            <Ban className="w-4 h-4 text-slate-500" aria-hidden="true" />
                          </div>
                          <div>
                            <p className="text-base font-bold text-slate-800">FREE</p>
                            <p className="text-xs text-slate-500">No subscription</p>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-black text-slate-800">{sym}0</span>
                          <span className="text-sm text-slate-500">/ always</span>
                        </div>
                      </div>
                      <div className="flex-1 px-5 pb-5 pt-4 border-t border-slate-100 space-y-2.5">
                        <Feature label="Analytics" value="Basic" />
                        <Feature label="Offerings" value={formatOfferingTypes(plansData?.baseline?.allowedOfferingTypes)} />
                        <Feature label="Ranking boost" value="None" muted />
                        <Feature label="Bonus coins" value="None" muted />
                      </div>
                      <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                          Always free
                        </span>
                      </div>
                    </div>

                    {/* Tier plan cards */}
                    {sortedPlans.map((plan) => {
                      const highlight = columnHighlight(plan.tier, currentTier, trialActive);
                      const isTrialColumn = highlight === "trial";
                      const isScheduledColumn = scheduledSubscription?.tier === plan.tier;
                      const TierIcon = TIER_ICONS[plan.tier];
                      const colors = TIER_HEADER_COLORS[plan.tier];
                      const gradient = TIER_GRADIENTS[plan.tier];

                      const borderClass = isTrialColumn
                        ? "border-2 border-dashed border-violet-500 shadow-lg"
                        : highlight === "current"
                        ? plan.tier === "SILVER"   ? "border-2 border-slate-400 shadow-lg"
                        : plan.tier === "GOLD"     ? "border-2 border-amber-400 shadow-lg"
                        :                            "border-2 border-violet-500 shadow-lg"
                        : highlight === "recommended"
                        ? "border-2 border-violet-400 shadow-md"
                        : "border border-slate-200";

                      return (
                        <div key={plan.planId} className={cn("relative flex flex-col rounded-2xl bg-white overflow-hidden", borderClass)}>
                          {/* Tier color top bar — replaced, on the trial column, by the filled
                              strip that names the trial and its end date in words. */}
                          {isTrialColumn && trial ? (
                            <div className="flex items-center justify-center gap-1.5 bg-violet-600 px-3 py-1.5 text-xs font-bold text-white">
                              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
                              Free trial — ends {formatTrialDate(trial.endsAt)}
                            </div>
                          ) : (
                            <div className={`h-1.5 bg-gradient-to-r ${gradient}`} />
                          )}

                          {/* Header */}
                          <div className="px-5 pt-5 pb-4">
                            {/* Badge */}
                            {highlight === "current" && (
                              <span className={cn("inline-block mb-3 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white", colors.badge)}>
                                Your plan
                              </span>
                            )}
                            {highlight === "recommended" && (
                              <span className="inline-block mb-3 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white bg-violet-600">
                                Recommended
                              </span>
                            )}
                            {isScheduledColumn && (
                              <span className="inline-flex items-center gap-1 mb-3 ml-2 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white bg-emerald-600">
                                <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                                Scheduled
                              </span>
                            )}

                            <div className="flex items-center gap-2.5 mb-4">
                              <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
                                plan.tier === "SILVER"   ? "bg-slate-100" :
                                plan.tier === "GOLD"     ? "bg-amber-50" : "bg-violet-50"
                              )}>
                                <TierIcon className={cn("w-5 h-5", colors.icon)} aria-hidden="true" />
                              </div>
                              <div>
                                <p className={cn("text-base font-bold", colors.label)}>{plan.tier}</p>
                                <p className="text-xs text-slate-400">
                                  {plan.tier === "SILVER" ? "Get started" : plan.tier === "GOLD" ? "Most popular" : "Full power"}
                                </p>
                              </div>
                            </div>

                            <PlanPriceCell plan={plan} billingCycle={billingCycle} sym={sym} />
                          </div>

                          {/* Features */}
                          <div className="flex-1 px-5 pb-5 pt-4 border-t border-slate-100 space-y-2.5">
                            {isTrialColumn ? (
                              /* AC#8 — mandatory disclosure: the trial grants Platinum's limits but
                                 NOT its search boost. Stated in words next to an icon, never left
                                 to a muted colour. */
                              <Feature
                                text={`Boost +${plan.boostScore} (not included during trial)`}
                                icon={<Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />}
                              />
                            ) : (
                              <Feature
                                label="Ranking boost"
                                value={String(plan.boostScore)}
                                icon={<Zap className="w-4 h-4 text-violet-500 shrink-0" aria-hidden="true" />}
                              />
                            )}
                            <Feature label="Analytics" value={formatAnalyticsAccess(plan.tier)} />
                            {tierBenefitsOn && (
                              <Feature label="Offerings" value={formatOfferingTypes(plan.allowedOfferingTypes)} />
                            )}
                            {tierBenefitsOn && plan.bonusCoinsPerMonth ? (
                              <Feature label="Bonus coins" value={formatBonusCoins(plan.bonusCoinsPerMonth, billingCycle)} />
                            ) : tierBenefitsOn ? (
                              <Feature label="Bonus coins" value="None" muted />
                            ) : null}
                            {tierBenefitsOn && (plan.coinDiscountPct ?? 0) > 0 && (
                              <Feature label="Reply discount" value={formatCoinDiscount(plan.coinDiscountPct)} />
                            )}
                            {tierBenefitsOn && (
                              <Feature label="Max offerings" value={formatMaxOfferings(plan.maxPublishedOfferings)} />
                            )}
                          </div>

                          {/* Action */}
                          <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                            <PlanActionCell
                              plan={plan}
                              subscription={subscription}
                              trial={trialActive ? trial : null}
                              pendingChange={pendingChange}
                              boostOverridden={boostOverridden}
                              billingCycle={billingCycle}
                              sym={sym}
                              autoRenewChoiceOn={autoRenewChoiceOn}
                              subscribeToTier={subscribeToTier}
                              scheduleAtTrialEnd={scheduleAtTrialEnd}
                              upgradeTier={upgradeTier}
                              renewSubscription={renewSubscription}
                              scheduleDowngrade={scheduleDowngrade}
                              cancelSubscription={cancelSubscription}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* F5: ad-boost purchase flow — always visible; the card handles the ineligible state internally */}
                <AdBoostPurchaseCard />
              </TabsContent>

              <TabsContent value="history" className="mt-4 space-y-6">
                {!tierBenefitsOn ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                    <p className="text-sm text-slate-500">Subscription history is coming soon.</p>
                  </div>
                ) : historyLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-40 w-full rounded-2xl" />
                    <Skeleton className="h-64 w-full rounded-2xl" />
                  </div>
                ) : (
                  <>
                    {/* ── F13: periods -- card list ── */}
                    <div>
                      <div className="flex items-center gap-2 mb-3 px-0.5">
                        <h2 className="text-sm font-bold text-slate-800">Subscription periods</h2>
                        {historyData?.periods.length ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            {historyData.periods.length}
                          </span>
                        ) : null}
                      </div>
                      {!historyData?.periods.length ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                          <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" aria-hidden="true" />
                          <p className="text-sm text-slate-500">No subscription periods yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {historyData.periods.map((period) => {
                            const TierIcon = TIER_ICONS[period.tier as SubscriptionTier] ?? Medal;
                            const colors = TIER_HEADER_COLORS[period.tier as SubscriptionTier] ?? TIER_HEADER_COLORS.SILVER;
                            const gradient = TIER_GRADIENTS[period.tier as SubscriptionTier] ?? TIER_GRADIENTS.SILVER;
                            const isOngoing = !period.endDate || period.endedBy === "ACTIVE" || period.endedBy === "CANCEL_PENDING";
                            return (
                              <div key={period.periodId} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                                {/* Tier icon */}
                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0`}>
                                  <TierIcon className="w-5 h-5 text-white" aria-hidden="true" />
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={cn("text-sm font-bold", colors.label)}>{period.tier}</span>
                                    <PeriodStatusBadge endedBy={period.endedBy} />
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400 flex-wrap">
                                    <span>{formatDate(period.startDate)}</span>
                                    <span>→</span>
                                    <span className={isOngoing ? "text-emerald-600 font-medium" : ""}>
                                      {period.endDate ? formatDate(period.endDate) : "Ongoing"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── F13: events timeline ── */}
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 px-0.5">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-bold text-slate-800">Activity timeline</h2>
                          {historyData?.events.content.length ? (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {historyData.events.totalElements}
                            </span>
                          ) : null}
                        </div>
                        {eventTypeOptions.length > 0 && (
                          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                            <SelectTrigger className="w-full sm:w-52 cursor-pointer text-sm">
                              <SelectValue placeholder="All events" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ALL" className="cursor-pointer">All events</SelectItem>
                              {eventTypeOptions.map((type) => (
                                <SelectItem key={type} value={type} className="cursor-pointer">
                                  {formatCode(type)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {!historyData?.events.content.length ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                          <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" aria-hidden="true" />
                          <p className="text-sm text-slate-500">No activity yet.</p>
                        </div>
                      ) : filteredEvents.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                          <p className="text-sm text-slate-500">No events match this filter.</p>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                          <ul>
                            {filteredEvents.map((event) => (
                              <EventRow key={event.id} event={event} />
                            ))}
                          </ul>
                          {historyData && historyData.events.totalPages > 1 && (
                            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/50">
                              <Button
                                variant="outline"
                                size="sm"
                                className="cursor-pointer transition-colors duration-200"
                                disabled={historyData.events.first}
                                onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                              >
                                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                                Previous
                              </Button>
                              <span className="text-xs text-slate-400">
                                Page {historyData.events.number + 1} of {historyData.events.totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="cursor-pointer transition-colors duration-200"
                                disabled={historyData.events.last}
                                onClick={() => setHistoryPage((p) => p + 1)}
                              >
                                Next
                                <ChevronRight className="w-4 h-4" aria-hidden="true" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </main>
        </div>
      </div>
      </SidebarProvider>
    </div>
  );
}
